import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLogs, manualSync } from '../../redux/biometricSlice';
import { Activity, Clock, Search, Calendar, X } from 'lucide-react';
import Loader from '../../components/ui/Loader';

const CustomTimeSelect = ({ value, onChange, options, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = React.useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);

    return (
        <div className="relative" ref={containerRef}>
            <div 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full pl-10 pr-8 py-3 rounded-xl border border-slate-200 hover:border-emerald-300 transition-all text-sm font-medium text-slate-700 bg-white cursor-pointer select-none flex items-center h-[46px]"
            >
                {selectedOption ? selectedOption.label : placeholder}
                <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-slate-400">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path></svg>
                </div>
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-slate-100 max-h-56 overflow-y-auto py-2">
                    <div 
                        className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer text-sm text-slate-700 transition-colors"
                        onClick={() => { onChange(''); setIsOpen(false); }}
                    >
                        {placeholder}
                    </div>
                    {options.map((t) => (
                        <div 
                            key={t.value}
                            className={`px-4 py-2.5 cursor-pointer text-sm transition-colors ${value === t.value ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-700 hover:bg-slate-50'}`}
                            onClick={() => { onChange(t.value); setIsOpen(false); }}
                        >
                            {t.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const SyncLogs = () => {
    const dispatch = useDispatch();
    const { logs, loading } = useSelector((state) => state.biometric);
    const { user } = useSelector((state) => state.auth);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [fromTime, setFromTime] = useState('');
    const [toTime, setToTime] = useState('');

    useEffect(() => {
        dispatch(fetchLogs(user?.token));
    }, [dispatch, user?.token]);

    // Extract unique devices and employees for dropdowns
    const uniqueDevices = [...new Set(logs.map(log => log.deviceId?.deviceName).filter(Boolean))];
    const uniqueEmployees = [...new Set(logs.map(log => log.employeeNo).filter(Boolean))].sort((a, b) => {
        // Try to sort numerically if possible
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
    });

    const timeOptions = [];
    for (let i = 0; i < 24; i++) {
        for (let j = 0; j < 60; j += 30) {
            const hour = i.toString().padStart(2, '0');
            const min = j.toString().padStart(2, '0');
            const ampm = i >= 12 ? 'PM' : 'AM';
            const displayH = i % 12 || 12;
            timeOptions.push({
                value: `${hour}:${min}`,
                label: `${displayH}:${min} ${ampm}`
            });
        }
    }

    const filteredLogs = logs.filter(log => {
        const matchDevice = selectedDevice === '' || log.deviceId?.deviceName === selectedDevice;
        const matchEmployee = selectedEmployee === '' || log.employeeNo === selectedEmployee;
        
        let matchDate = true;
        let matchTime = true;
        
        if (fromDate || toDate || fromTime || toTime) {
            const eventDateObj = new Date(log.eventTime);
            
            // Date matching
            const eventDateStr = eventDateObj.toISOString().split('T')[0];
            if (fromDate && eventDateStr < fromDate) matchDate = false;
            if (toDate && eventDateStr > toDate) matchDate = false;

            // Time matching
            const hours = eventDateObj.getHours().toString().padStart(2, '0');
            const minutes = eventDateObj.getMinutes().toString().padStart(2, '0');
            const eventTimeStr = `${hours}:${minutes}`;
            
            if (fromTime && eventTimeStr < fromTime) matchTime = false;
            if (toTime && eventTimeStr > toTime) matchTime = false;
        }

        return matchDevice && matchEmployee && matchDate && matchTime;
    });

    const clearFilters = () => {
        setFromDate('');
        setToDate('');
        setFromTime('');
        setToTime('');
        setSelectedDevice('');
        setSelectedEmployee('');
    };

    return (
        <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto p-4 md:p-8">
            <div className="glass-card rounded-[2.5rem] p-8 flex flex-col gap-6 premium-shadow">
                <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="text-center md:text-left">
                        <h1 className="text-4xl font-black tracking-tight bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
                            Biometric Sync Logs
                        </h1>
                        <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
                            <Activity size={18} className="text-emerald-600" />
                            History of successfully synced attendance events
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-3">
                        <button 
                            onClick={clearFilters}
                            disabled={!(fromDate || toDate || fromTime || toTime || selectedDevice || selectedEmployee)}
                            className={`w-full sm:w-auto px-6 py-3 rounded-xl transition-all font-bold flex items-center justify-center gap-2 shadow-sm border ${
                                (fromDate || toDate || fromTime || toTime || selectedDevice || selectedEmployee)
                                    ? 'bg-red-50 text-red-600 hover:bg-red-100 border-red-100'
                                    : 'bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed'
                            }`}
                        >
                            <X size={20} /> Clear Filters
                        </button>
                        <button 
                            onClick={async () => {
                                await dispatch(manualSync(user?.token)).unwrap();
                                dispatch(fetchLogs(user?.token));
                            }} 
                            disabled={loading}
                            className={`w-full sm:w-auto px-6 py-3 bg-white text-emerald-700 hover:bg-emerald-50 rounded-xl transition-all font-bold flex items-center justify-center gap-2 shadow-sm border border-emerald-100 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            <Clock size={20} className={loading ? 'animate-spin' : ''} /> Refresh Logs
                        </button>
                    </div>
                </div>
                
                {/* Filters Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 pt-6 border-t border-slate-100/50">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">From Date</label>
                        <div className="relative">
                            <input 
                                type="date" 
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm font-medium text-slate-700 bg-white cursor-pointer"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">To Date</label>
                        <div className="relative">
                            <input 
                                type="date" 
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm font-medium text-slate-700 bg-white cursor-pointer"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">From Time</label>
                        <CustomTimeSelect 
                            value={fromTime}
                            onChange={setFromTime}
                            options={timeOptions}
                            placeholder="Any Time"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">To Time</label>
                        <CustomTimeSelect 
                            value={toTime}
                            onChange={setToTime}
                            options={timeOptions}
                            placeholder="Any Time"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Device Filter</label>
                        <select
                            value={selectedDevice}
                            onChange={(e) => setSelectedDevice(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm font-medium text-slate-700 bg-white"
                        >
                            <option value="">All Devices</option>
                            {uniqueDevices.map(device => (
                                <option key={device} value={device}>{device}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Employee Filter</label>
                        <select
                            value={selectedEmployee}
                            onChange={(e) => setSelectedEmployee(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm font-medium text-slate-700 bg-white"
                        >
                            <option value="">All Employees</option>
                            {uniqueEmployees.map(emp => (
                                <option key={emp} value={emp}>Emp No: {emp}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {loading ? <Loader /> : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="p-4 text-sm font-bold text-slate-600 uppercase tracking-wider">Date</th>
                                    <th className="p-4 text-sm font-bold text-slate-600 uppercase tracking-wider">Time</th>
                                    <th className="p-4 text-sm font-bold text-slate-600 uppercase tracking-wider">Device</th>
                                    <th className="p-4 text-sm font-bold text-slate-600 uppercase tracking-wider">Employee No</th>
                                    <th className="p-4 text-sm font-bold text-slate-600 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {filteredLogs.map(log => (
                                    <tr key={log._id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="p-4 text-sm text-slate-700 font-medium">
                                            {new Date(log.eventTime).toLocaleDateString()}
                                        </td>
                                        <td className="p-4 text-sm text-slate-700 font-medium">
                                            {new Date(log.eventTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td className="p-4 text-sm text-slate-600">
                                            {log.deviceId?.deviceName} <span className="text-xs text-slate-400">({log.deviceId?.ipAddress})</span>
                                        </td>
                                        <td className="p-4 text-sm font-mono text-slate-800">
                                            {log.employeeNo}
                                        </td>
                                        <td className="p-4">
                                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                                                SYNCED
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {filteredLogs.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="p-12 text-center text-slate-500">
                                            {(selectedDevice || selectedEmployee || fromDate || toDate || fromTime || toTime) ? 'No logs match your filter criteria.' : 'No sync logs found.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SyncLogs;
